/**
 * AdminRoute.jsx
 *
 * Route guard that allows access only to authenticated users with is_admin = true.
 *
 * - While session/profile is loading: shows a spinner
 * - If not authenticated: redirects to /auth
 * - If authenticated but not admin: shows an access-denied screen
 * - If admin: renders children
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { ShieldOff } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const AdminRoute = ({ children }) => {
  const { user, userProfile, loading, profileLoading } = useAuth();
  const location = useLocation();

  // Show spinner while session or profile is being loaded
  if (loading || profileLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
          <p className="text-sm text-gray-500">Checking permissions...</p>
        </div>
      </div>
    );
  }

  // Redirect unauthenticated users to /auth
  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  // Show access-denied screen for authenticated non-admin users
  if (!userProfile?.is_admin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md px-6">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <ShieldOff className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-500 mb-6">
            You need administrator privileges to access the Blog Editor. Contact the site owner to request access.
          </p>
          <a
            href="/user-dashboard"
            className="inline-flex items-center px-5 py-2.5 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors"
          >
            Back to Dashboard
          </a>
        </div>
      </div>
    );
  }

  return children;
};

export default AdminRoute;
